import * as React from "react";
import { MesoCollapsibleGroupRegionProps } from "./typings";
import {
  BlossomComponentFactory,
  BlossomComponentFactoryProps,
} from "components/flexFramework/domain/BlossomComponentFactory";
import { logger } from "bernie-core";
import { inject, observer } from "mobx-react";
import { MESO_AD_COLLAPSE } from "config/systemEvents";
import { MarqueeSingleCardView } from "./MarqueeSingleCardView/MarqueeSingleCardView";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { isVariantEnabled, Experiment, getExperimentBucket } from "src/components/utility/experiment";
import { MarqueeGridView } from "./MarqueeGridView/MarqueeGridView";
import { SpacingSize } from "src/components/utility/FlexAttributesUtil";

/**
 * Meso Ad Group Region wraps its ad products and collapses the region if the 0 paid ads are return from Google Ad Manager
 * @see https://pages.github.expedia.biz/Flex/blossom-flex-ui-documentation/docs/Domains_within_Blossom/Storefront_BEX_+_COMET/Native_Ad_Container/
 * @see https://pages.github.expedia.biz/Flex/blossom-flex-ui-documentation/docs/Domains_within_Blossom/Storefront_BEX_+_COMET/Meso_Ad_Group/
 * @see https://github.expedia.biz/MediaSolutions/ads-delivery-service/wiki
 */
const mesoAdGroupEventName = "collapseAdGroup";

enum TEST_BUCKET {
  NON_COLLAPSIBLE_AD = 0,
  NO_CTA_BUTTON = 1,
  CTA_BUTTON = 2,
  CTA_TEXT_LINK = 3,
  COLLAPSIBLE_AD = 4,
}

export const MesoCollapsibleGroupRegion: React.FC<MesoCollapsibleGroupRegionProps> = logger(
  "MesoCollapsibleGroupRegion"
)(
  inject("advertising")(
    observer((props: MesoCollapsibleGroupRegionProps) => {
      const blossomComponent = BlossomComponentFactory.get(props as BlossomComponentFactoryProps);
      const mesoAdGroup: React.RefObject<JSX.IntrinsicElements["meso-collapsible-group"]> = React.createRef();
      const [collapsed, setCollapsed] = React.useState(false);
      const { context, flexComponent, advertising, l10n } = props;
      const { title, innerTitle, padding } = flexComponent.model;

      const removeAdLoadEventHandler = () => {
        if (mesoAdGroup && mesoAdGroup.current) {
          mesoAdGroup.current.removeEventListener(mesoAdGroupEventName, handleAdGroupEvent);
        }
      };

      const handleAdGroupEvent = (event: CustomEvent) => {
        const details = (event && event.detail) || {};
        if (details.type === "meso-collapsible-group-event") {
          if (details.collapse) collapseAdGroup();
        }
      };

      const collapseAdGroup = (message?: string) => {
        setCollapsed(true);
        props.logger.logEvent(MESO_AD_COLLAPSE, `Meso Ad Group Collapsed - ${message}`);
        removeAdLoadEventHandler();
      };

      React.useEffect(() => {
        if (props.advertising) {
          props.advertising.includeAd();
        }
        if (mesoAdGroup && mesoAdGroup.current) {
          window.addEventListener(mesoAdGroupEventName, handleAdGroupEvent);
          mesoAdGroup.current.addEventListener(mesoAdGroupEventName, handleAdGroupEvent);
        }
        return removeAdLoadEventHandler;
      }, [mesoAdGroup.current]);

      /* istanbul ignore next */
      advertising?.includeAd?.();

      if (
        !flexComponent ||
        !flexComponent.model ||
        !blossomComponent ||
        (advertising && advertising.adsSuppressed) ||
        (advertising && advertising.adBlockerEnabled === true) ||
        collapsed
      ) {
        return null;
      }

      const config = {
        control: {
          UCIs: ["NMC1", "NMC2", "NMC3"],
          isGridView: true,
        },
        defaultInVariant: {
          UCIs: ["NMC1Control", "NMC2Control", "NMC3Control"],
          isGridView: true,
        },
        [TEST_BUCKET.NO_CTA_BUTTON]: {
          UCIs: ["NMC1Test1", "NMC2", "NMC3"],
          isGridView: false,
        },
        [TEST_BUCKET.CTA_BUTTON]: {
          UCIs: ["NMC1Test2", "NMC2", "NMC3"],
          isGridView: false,
        },
        [TEST_BUCKET.CTA_TEXT_LINK]: {
          UCIs: ["NMC1Test3", "NMC2", "NMC3"],
          isGridView: false,
        },
        [TEST_BUCKET.COLLAPSIBLE_AD]: {
          UCIs: ["NMC1", "NMC2", "NMC3"],
          isGridView: true,
        },
      };

      const { UCIs, isGridView } = isVariantEnabled(context, Experiment.MeSo_Single_Card_Feature_Flag, 1)
        ? config[getExperimentBucket(context, Experiment.MeSo_Single_Card_Marquee)] || config.defaultInVariant
        : config.control;

      const hasNoPadding = padding === SpacingSize.NONE;
      const blockStartSpacing = hasNoPadding ? "unset" : "six";

      return (
        <UitkSpacing padding={{ blockstart: blockStartSpacing }}>
          <UitkLayoutPosition>
            <meso-collapsible-group data-testid="meso-collapsible-group" ref={mesoAdGroup}>
              {isGridView ? (
                <MarqueeGridView
                  title={title}
                  innerTitle={innerTitle}
                  UCIs={UCIs}
                  context={context}
                  l10n={l10n}
                  flexComponent={flexComponent}
                />
              ) : (
                <MarqueeSingleCardView
                  title={title}
                  innerTitle={innerTitle}
                  context={context}
                  l10n={l10n}
                  flexComponent={flexComponent}
                  uci={UCIs[0]}
                />
              )}
            </meso-collapsible-group>
          </UitkLayoutPosition>
        </UitkSpacing>
      );
    })
  )
);
export default MesoCollapsibleGroupRegion;
